import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import axios from "axios";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { SiteContext } from "./context/SiteContext";
import { API, DEFAULT_SETTINGS, FALLBACK_ROOMS } from "./lib/site";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { FloatingCTA } from "./components/FloatingCTA";
import { BookingModal } from "./components/BookingModal";
import Home from "./pages/Home";
import Rooms from "./pages/Rooms";
import Spa from "./pages/Spa";
import Dining from "./pages/Dining";
import Gallery from "./pages/Gallery";
import Experiences from "./pages/Experiences";
import Offers from "./pages/Offers";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Policies from "./pages/Policies";
import Privacy from "./pages/Privacy";
import Admin from "./pages/Admin";
import "@/App.css";

const TITLES = {
  "/": "Madhubhan Resort & Spa | Luxury Resort in Anand, Gujarat — Book Direct",
  "/rooms": "Rooms & Suites | Madhubhan Resort & Spa, Anand",
  "/spa": "Resort & Spa | Madhubhan Resort & Spa, Anand",
  "/dining": "Dining | Madhubhan Resort & Spa, Anand",
  "/gallery": "Gallery | Madhubhan Resort & Spa, Anand",
  "/experiences": "Experiences | Madhubhan Resort & Spa, Anand",
  "/offers": "Offers & Packages | Madhubhan Resort & Spa, Anand",
  "/about": "About Us | Madhubhan Resort & Spa, Anand",
  "/contact": "Contact Us | Madhubhan Resort & Spa, Anand",
  "/policies": "Terms & Cancellation Policy | Madhubhan Resort & Spa, Anand",
  "/privacy": "Privacy Policy | Madhubhan Resort & Spa, Anand",
  "/admin": "Admin | Madhubhan Resort & Spa",
};

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLES[pathname] || TITLES["/"];
  }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const [rooms, setRooms] = useState(FALLBACK_ROOMS);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [gallery, setGallery] = useState([]);
  const [booking, setBooking] = useState(null);

  const loadRooms = useCallback(() => {
    axios.get(`${API}/rooms`).then((response) => setRooms(response.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadRooms();
    axios.get(`${API}/settings`).then((response) => setSettings({ ...DEFAULT_SETTINGS, ...response.data })).catch(() => {});
    axios.get(`${API}/gallery`).then((response) => setGallery(response.data)).catch(() => {});
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, [loadRooms]);

  useEffect(() => {
    const tagId = (settings.google_tag_id || "").trim();
    if (!tagId || document.getElementById("google-ads-tag")) return;
    if (tagId.startsWith("GTM-")) {
      const script = document.createElement("script");
      script.id = "google-ads-tag";
      script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${tagId}');`;
      document.head.appendChild(script);
    } else {
      const script = document.createElement("script");
      script.id = "google-ads-tag";
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
      document.head.appendChild(script);
      const inline = document.createElement("script");
      inline.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${tagId}');`;
      document.head.appendChild(inline);
    }
  }, [settings.google_tag_id]);

  return (
    <SiteContext.Provider value={{ rooms, settings, gallery, openBooking: setBooking, refreshRooms: loadRooms }}>
      <div className="site-shell">
        <Toaster position="top-right" richColors />
        {!isAdmin && <Navbar />}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/rooms" element={<Rooms />} />
          <Route path="/spa" element={<Spa />} />
          <Route path="/dining" element={<Dining />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/experiences" element={<Experiences />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/policies" element={<Policies />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
        {!isAdmin && <Footer />}
        {!isAdmin && <FloatingCTA />}
        {booking !== null && <BookingModal payload={booking} onClose={() => setBooking(null)} />}
      </div>
    </SiteContext.Provider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollManager />
      <Shell />
    </BrowserRouter>
  );
}
